/**
 * Custom fetch used by every Orval-generated operation.
 *
 * Orval calls this with the path it built from the OpenAPI spec plus a
 * standard `RequestInit`, and expects `{ status, data, headers }` back.
 * Everything cross-cutting (base URL, auth headers, error normalisation)
 * belongs here so the generated files stay untouched.
 *
 * The base URL defaults to `/api/v1`, which the web dev server proxies.
 * Call `setApiBaseUrl` once at startup to point somewhere else. `@ziggiz/web`
 * does that from `main.tsx` with `VITE_API_BASE_URL`.
 *
 * Credentials come from whatever `setApiAuth` registered. This package never
 * imports the app, so `@ziggiz/web` registers its Entra client at startup.
 * With nothing registered, requests go out without an `authorization` header.
 *
 * Non-2xx responses reject with an `ApiError` rather than resolving, so
 * React Query treats them as errors and the error boundaries pick them up.
 */
import { ApiError } from './api-error';

/** A bearer token bound to one sign-in session. */
export interface ApiCredentials {
  token: string;
  /** Aborts when the session that issued `token` ends. */
  signal: AbortSignal;
  /** Throws once the session that issued `token` is no longer current. */
  assertCurrent: () => void;
}

export interface ApiAuth {
  getCredentials: () => Promise<ApiCredentials>;
  /** Called on a 401. Must not replay the request or redirect on its own. */
  requireSignIn: () => void;
}

let baseUrl = '/api/v1';
let auth: ApiAuth | undefined;

/** Point every generated operation at `url`. A trailing slash is stripped. */
export const setApiBaseUrl = (url: string) => {
  baseUrl = url.replace(/\/+$/, '');
};

/** Attach credentials to every generated operation. Pass `undefined` to detach. */
export const setApiAuth = (next: ApiAuth | undefined) => {
  auth = next;
};

const parseBody = async (response: Response): Promise<unknown> => {
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined;
  }

  const contentType = response.headers.get('content-type')?.split(';')[0].trim() ?? '';
  if (
    contentType === 'application/json' ||
    contentType.endsWith('+json') ||
    contentType === 'application/vnd.goa.error'
  ) {
    return response.json();
  }

  if (contentType === 'application/zip' || contentType === 'application/vnd.sqlite3') {
    return response.blob();
  }

  return response.text();
};

const LOCAL_HOSTS = ['localhost', '127.0.0.1', '[::1]'];

/**
 * Resolves `path` against the base URL and refuses anything that would send
 * credentials somewhere other than the configured API.
 */
const buildUrl = (path: string) => {
  const origin = globalThis.location?.origin ?? 'http://localhost';
  const url = /^https?:\/\//.test(path) ? path : `${baseUrl}/${path.replace(/^\/+/, '')}`;
  const base = new URL(baseUrl || '/', origin);
  const destination = new URL(url, origin);
  const basePath = base.pathname.replace(/\/+$/, '');
  const localHttp = base.protocol === 'http:' && LOCAL_HOSTS.includes(base.hostname);

  if (
    (base.protocol !== 'https:' && !localHttp) ||
    base.username ||
    base.password ||
    base.search ||
    base.hash ||
    destination.origin !== base.origin ||
    destination.username ||
    destination.password ||
    destination.hash ||
    /%2f|%5c/i.test(destination.pathname) ||
    !(
      destination.pathname === basePath || destination.pathname.startsWith(`${basePath}/`)
    )
  ) {
    throw new ApiError('Request is outside the configured API.', 0, 'untrusted_api_url');
  }

  return url;
};

/**
 * Checks the destination, attaches credentials and sends. Shared by the
 * JSON client and the streaming one so both enforce the same rules.
 */
const send = async (path: string, init?: RequestInit) => {
  // Check the destination before acquiring any credentials.
  const url = buildUrl(path);
  init?.signal?.throwIfAborted();

  const credentials = auth ? await auth.getCredentials() : undefined;
  const signal =
    init?.signal && credentials
      ? AbortSignal.any([init.signal, credentials.signal])
      : (credentials?.signal ?? init?.signal ?? undefined);
  signal?.throwIfAborted();

  const headers = new Headers(init?.headers);
  if (!headers.has('content-type') && !(init?.body instanceof FormData)) {
    headers.set('content-type', 'application/json');
  }
  if (credentials) {
    headers.set('authorization', `Bearer ${credentials.token}`);
  } else {
    headers.delete('authorization');
  }

  const response = await fetch(url, {
    ...init,
    headers,
    signal,
    credentials: 'omit',
    // Never forward a bearer token through server-controlled redirects.
    redirect: 'error'
  });

  return { response, credentials, signal };
};

export const customFetch = async <T>(path: string, init?: RequestInit): Promise<T> => {
  const { response, credentials, signal } = await send(path, init);

  credentials?.assertCurrent();
  let data: unknown;
  try {
    data = await parseBody(response);
  } catch (error) {
    credentials?.assertCurrent();
    signal?.throwIfAborted();
    if (response.ok) throw error;
    // Even a malformed error response retains its HTTP status and recovery path.
  }
  credentials?.assertCurrent();
  signal?.throwIfAborted();
  // Read the response first: invalidating the session aborts its response stream.
  if (response.status === 401) auth?.requireSignIn();

  if (!response.ok) {
    throw ApiError.fromResponse(response, data);
  }

  return { status: response.status, data, headers: response.headers } as T;
};

/**
 * `fetch`-shaped access to a streaming endpoint (the Assistant's chat
 * stream). Same destination check and credentials as `customFetch`, but a
 * 2xx resolves with the untouched `Response` so the caller can read its body
 * as it arrives. The body aborts with the session that authorised it.
 */
export const streamingFetch = async (
  path: string,
  init?: RequestInit
): Promise<Response> => {
  const { response, credentials, signal } = await send(path, init);
  credentials?.assertCurrent();

  if (response.ok) {
    return response;
  }

  let data: unknown;
  try {
    data = await parseBody(response);
  } catch {
    // A malformed error body still carries the status.
  }
  credentials?.assertCurrent();
  signal?.throwIfAborted();
  if (response.status === 401) auth?.requireSignIn();
  throw ApiError.fromResponse(response, data);
};
